/**
 * OrdersRoutingPanel — the 'orders' area panel.
 *
 * One SettingsSection per `areas.ts` 'orders' registry entry. Sections that
 * already own their internal save behaviour (OrderQueue, SplitOrder,
 * BenchRouting, ProductTypeAlias, LineItemExclusions) are wrapped AS-IS;
 * TagFiltersSection and ShopifyTaggingSection use the Console dirty model.
 */
import { Film, Filter, GitBranch, Info, Layers, LayoutPanelTop, ListOrdered, Replace, Users, Zap } from 'lucide-react';
import LineItemExclusionsSettings from '@/features/line-item-exclusions/components/LineItemExclusionsSettings';
import { useTranslation } from 'react-i18next';
import { ExpressSettingsSection } from '../components/ExpressSettingsSection';
import { DtfOrderTagsSection } from '../components/DtfOrderTagsSection';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useLocation } from '@/contexts/LocationContext';
import { useRole } from '@/hooks/use-role';
import { NoteLine } from '@/components/shared/NoteLine';
import { SettingsSection } from '../components/console/SettingsSection';
import { OrderQueueSettingsSection } from '../components/OrderQueueSettingsSection';
import { OrdersDefaultClientSection } from '../components/OrdersDefaultClientSection';
import { BatchActionsSettingsSection } from '../components/BatchActionsSettingsSection';
import { SplitOrderSettingsSection } from '../components/SplitOrderSettingsSection';
import { BenchRoutingSection } from '../components/BenchRoutingSection';
import { ProductTypeAliasSection } from '../components/ProductTypeAliasSection';
import { TagFiltersSection } from '../sections/TagFiltersSection';
import { ShopifyTaggingSection } from '../sections/ShopifyTaggingSection';

export function OrdersRoutingPanel() {
  const { isAdmin } = useRole();
  const { organization } = useOrganization();
  const { selectedLocation } = useLocation();
  const { t } = useTranslation('settings');

  return (
    <>
      <SettingsSection
        id="order-queue"
        title="Order queue"
        description="Queue display mode, page size and queue tag filters."
        icon={ListOrdered}
        scope="org"
      >
        <OrderQueueSettingsSection embedded />
      </SettingsSection>

      {/* Per-location, bound to the header location like the Locations area's
          cards; keyed so a location switch starts it fresh. The card stays in
          place with no location picked, so a jump from settings search
          always has somewhere to land. */}
      <SettingsSection
        key={selectedLocation?.id ?? 'no-location'}
        id="orders-default-client"
        title={t('production.ordersDefaultClient.title')}
        description={t('production.ordersDefaultClient.description')}
        icon={Users}
        scope="location"
      >
        {selectedLocation ? (
          <OrdersDefaultClientSection locationId={selectedLocation.id} />
        ) : (
          <NoteLine icon={Info}>{t('production.ordersDefaultClient.noLocation')}</NoteLine>
        )}
      </SettingsSection>

      <SettingsSection
        id="express"
        title={t('express.title')}
        description={t('express.sectionDescription')}
        icon={Zap}
        scope="org"
      >
        <ExpressSettingsSection key={organization?.id} />
      </SettingsSection>

      <SettingsSection
        id="dtf-orders"
        title={t('dtfOrderTags.title')}
        description={t('dtfOrderTags.sectionDescription')}
        icon={Film}
        scope="org"
      >
        <DtfOrderTagsSection key={organization?.id} />
      </SettingsSection>

      {/* BLU-777 — sits directly under Order queue: it configures a control that
          lives on that same screen and draws on the tag vocabulary above it. */}
      <SettingsSection
        id="batch-buttons"
        title="Batch buttons"
        description="One-press batch groups for the Orders page, by order tag, or priority."
        icon={Layers}
        scope="org"
      >
        <BatchActionsSettingsSection embedded />
      </SettingsSection>

      <TagFiltersSection />

      {isAdmin() ? (
        <SettingsSection
          id="line-item-exclusions"
          title="Line-item exclusions"
          description="Skip Shopify line items by tag, product type, product or variant."
          icon={Filter}
          scope="org"
        >
          <LineItemExclusionsSettings />
        </SettingsSection>
      ) : null}

      <SettingsSection
        id="split-orders"
        title="Split orders"
        description="Auto-accept split orders that carry configured Shopify tags."
        icon={GitBranch}
        scope="org"
      >
        <SplitOrderSettingsSection />
      </SettingsSection>

      <SettingsSection
        id="bench-routing"
        title="Bench routing"
        description="Route B2B and large orders to a work bench instead of a bin."
        icon={LayoutPanelTop}
        scope="org"
      >
        <BenchRoutingSection />
      </SettingsSection>

      <SettingsSection
        id="product-type-aliases"
        title="Product type aliases"
        description="Remap Shopify product types to custom picklist display names."
        icon={Replace}
        scope="org"
      >
        <ProductTypeAliasSection embedded />
      </SettingsSection>

      <ShopifyTaggingSection />
    </>
  );
}
